import React from 'react';
import { Box, Typography } from '@mui/joy';
import type { ContractField } from '../../../types';

interface CompletedFieldRendererProps {
  fields: ContractField[];
  fieldValues: Record<string, any>;
  scale: number;
}

/**
 * Renders completed contract fields in read-only mode with actual values displayed.
 * Unlike SigningFieldRenderer, this shows:
 * - Full text values (not truncated)
 * - Actual signature images (not just "✓ Signed")
 * - Properly formatted dates
 * - Clear checkbox states
 */
export const CompletedFieldRenderer: React.FC<CompletedFieldRendererProps> = ({
  fields,
  fieldValues,
  scale
}) => {
  const renderFieldContent = (field: ContractField) => {
    const value = fieldValues[field.id];
    
    // Don't render anything if no value
    if (value === undefined || value === '' || value === null) {
      return null;
    }

    switch (field.type) {
      case 'signature':
      case 'initial':
        // Render actual signature/initial image
        if (typeof value === 'string' && value.startsWith('data:image')) {
          return (
            <img
              src={value}
              alt={field.type === 'signature' ? 'Signature' : 'Initial'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                pointerEvents: 'none'
              }}
            />
          );
        }
        return null;

      case 'checkbox':
        // Show a clear checked/unchecked state
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: `${Math.max(14, 16 * scale)}px`,
              fontWeight: 'bold',
              color: value ? 'success.600' : 'neutral.400'
            }}
          >
            {value ? '✓' : '☐'}
          </Box>
        );

      case 'date':
        // Format date nicely
        try {
          const date = new Date(value);
          return (
            <Typography
              level="body-xs"
              sx={{
                fontSize: `${Math.max(10, 12 * scale)}px`,
                color: 'neutral.800',
                padding: `${2 * scale}px`,
                lineHeight: 1.2
              }}
            >
              {date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })}
            </Typography>
          );
        } catch {
          return (
            <Typography
              level="body-xs"
              sx={{
                fontSize: `${Math.max(10, 12 * scale)}px`,
                color: 'neutral.800',
                padding: `${2 * scale}px`
              }}
            >
              {String(value)}
            </Typography>
          );
        }

      case 'text':
      case 'dropdown':
      case 'radio':
      default:
        // Show full text value, not truncated
        return (
          <Typography
            level="body-xs"
            sx={{
              fontSize: `${Math.max(10, 12 * scale)}px`,
              color: 'neutral.800',
              padding: `${2 * scale}px`,
              lineHeight: 1.2,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word'
            }}
          >
            {String(value)}
          </Typography>
        );
    }
  };

  return (
    <>
      {fields.map((field) => {
        const value = fieldValues[field.id];
        
        // Skip fields with no value
        if (value === undefined || value === '' || value === null) {
          return null;
        }

        // For signature and initial fields, we want a transparent background
        const isSignatureField = field.type === 'signature' || field.type === 'initial';
        
        return (
          <Box
            key={field.id}
            sx={{
              position: 'absolute',
              left: field.x * scale,
              top: field.y * scale,
              width: field.width * scale,
              height: field.height * scale,
              backgroundColor: isSignatureField ? 'transparent' : 'rgba(255, 255, 255, 0.9)',
              border: isSignatureField ? 'none' : '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: isSignatureField ? 0 : '2px',
              overflow: 'hidden',
              pointerEvents: 'none', // Read-only
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {renderFieldContent(field)}
          </Box>
        );
      })}
    </>
  );
};